/**
 * useResourceGeolocations Hook
 * Merges live geolocation data with resource (driver/technician) data
 * for displaying on the Dispatch map
 */
import { useMemo, useCallback } from "react";
import { useGeolocation } from "../contexts/GeolocationContext";
import { useGetGeolocationsQuery } from "../redux/services/geolocations/geolocationApi";

/**
 * Merge resources with their live geolocation data
 * @param {Array} resources - Array of resource objects (drivers/technicians)
 * @returns {Object} - Object containing merged resources and helper methods
 */
export const useResourceGeolocations = (resources = []) => {
  const {
    geolocations: contextGeolocations,
    isLoading: contextIsLoading,
    isConnected,
    isSubscribed,
    getGeolocation,
    isLocationFresh,
    refreshGeolocations,
  } = useGeolocation();

  // Extract valid userIds from resources
  const userIds = useMemo(() => {
    if (!resources || resources.length === 0) return undefined;
    
    const ids = resources
      .map((resource) => {
        return (
          resource._id ||
          resource.id ||
          resource.userId ||
          resource.user?._id ||
          resource.user?.id
        );
      })
      .filter(
        (id) =>
          id != null &&
          id !== "undefined" &&
          id !== "null" &&
          typeof id === "string" &&
          id.trim().length > 0
      );

    return ids.length > 0 ? ids : undefined;
  }, [resources]);

  // Query geolocations for specific user IDs if available
  const {
    data: resourceGeolocations,
    isLoading: resourceIsLoading,
  } = useGetGeolocationsQuery(
    { userIds, freshnessMinutes: 30 },
    {
      skip: !userIds || userIds.length === 0,
    }
  );

  // Merge context geolocations with resource-specific geolocations
  const geolocations = useMemo(() => {
    const merged = { ...contextGeolocations };
    
    if (resourceGeolocations && Array.isArray(resourceGeolocations)) {
      resourceGeolocations.forEach((geo) => {
        if (geo.userId && geo.location) {
          merged[geo.userId] = {
            userId: geo.userId,
            fullName: geo.fullName,
            avatar: geo.avatar,
            location: geo.location,
            accuracy: geo.accuracy,
            heading: geo.heading,
            speed: geo.speed,
            timestamp: geo.timestamp,
            updatedAt: geo.updatedAt,
          };
        }
      });
    }
    
    return merged;
  }, [contextGeolocations, resourceGeolocations]);

  const isLoading = contextIsLoading || resourceIsLoading;

  /**
   * Resources with live location data merged in
   * Adds currentLocation property to each resource that has a fresh geolocation
   */
  const resourcesWithLocations = useMemo(() => {
    if (!resources || resources.length === 0) return [];

    return resources.map((resource) => {
      // Get user ID from resource (could be _id, id, userId, or resource itself is the user)
      const userId =
        resource._id ||
        resource.id ||
        resource.userId ||
        resource.user?._id ||
        resource.user?.id;

      if (!userId) return resource;

      // Get geolocation for this user (from merged geolocations)
      const geo = geolocations[userId] || getGeolocation(userId);

      // Check if location is fresh
      const hasFreshLocation = geo && isLocationFresh(geo.updatedAt);

      // Always return resource with all original properties preserved
      // Add location data if available
      if (hasFreshLocation) {
        return {
          ...resource,
          currentLocation: {
            lat: geo.location?.lat,
            lng: geo.location?.lng,
          },
          geolocationMeta: {
            accuracy: geo.accuracy,
            heading: geo.heading,
            speed: geo.speed,
            timestamp: geo.timestamp,
            updatedAt: geo.updatedAt,
          },
        };
      }

      // Return resource as-is (preserves hasActiveShift and other properties)
      return resource;
    });
  }, [resources, geolocations, getGeolocation, isLocationFresh]);

  /**
   * Get only resources that have fresh location data
   */
  const resourcesWithActiveLocations = useMemo(() => {
    return resourcesWithLocations.filter(
      (resource) => resource.currentLocation?.lat && resource.currentLocation?.lng
    );
  }, [resourcesWithLocations]);

  /**
   * Get geolocation for a specific resource by ID
   */
  const getResourceGeolocation = useCallback(
    (resourceId) => {
      return geolocations[resourceId] || getGeolocation(resourceId);
    },
    [geolocations, getGeolocation]
  );

  /**
   * Check if a specific resource has fresh location
   */
  const hasResourceLocation = useCallback(
    (resourceId) => {
      const geo = geolocations[resourceId] || getGeolocation(resourceId);
      return geo && isLocationFresh(geo.updatedAt);
    },
    [geolocations, getGeolocation, isLocationFresh]
  );

  /**
   * Get count of resources with active locations
   */
  const activeLocationCount = useMemo(() => {
    return resourcesWithActiveLocations.length;
  }, [resourcesWithActiveLocations]);

  return {
    // Merged data
    resourcesWithLocations,
    resourcesWithActiveLocations,
    activeLocationCount,

    // Helper methods
    getResourceGeolocation,
    hasResourceLocation,
    refreshGeolocations,

    // Connection status
    isLoading,
    isConnected,
    isSubscribed,
  };
};

export default useResourceGeolocations;

